import type { Participant, Room } from "./types";

export type RoomSocketStatus =
  "connecting" | "open" | "reconnecting" | "closed";

export type ServerMessage =
  | {
      type: "room:joined";
      room: Room;
      participants: Participant[];
      selfId: string;
      role: "host" | "viewer";
    }
  | { type: "room:updated"; room: Room; participants: Participant[] }
  | { type: "peer:joined"; peer: Participant }
  | { type: "peer:left"; peerId: string; role: "host" | "viewer" }
  | {
      type: "webrtc:offer";
      fromUserId: string;
      sdp: { type: "offer"; sdp: string };
    }
  | {
      type: "webrtc:answer";
      fromUserId: string;
      sdp: { type: "answer"; sdp: string };
    }
  | {
      type: "webrtc:ice";
      fromUserId: string;
      candidate: RTCIceCandidateInit | null;
    }
  | { type: "webrtc:restart-request"; fromUserId: string }
  | { type: "room:closed"; roomId: string; reason: string }
  | { type: "viewer:kicked"; roomId: string; reason: string }
  | { type: "room:left" }
  | { type: "error"; code: string; message: string; details?: unknown }
  | { type: "pong" };

export type OutboundMessage =
  | {
      type: "webrtc:offer";
      roomId: string;
      targetUserId: string;
      sdp: { type: "offer"; sdp: string };
    }
  | {
      type: "webrtc:answer";
      roomId: string;
      targetUserId: string;
      sdp: { type: "answer"; sdp: string };
    }
  | {
      type: "webrtc:ice";
      roomId: string;
      targetUserId: string;
      candidate: RTCIceCandidateInit | null;
    }
  | { type: "webrtc:restart-request"; roomId: string; targetUserId: string }
  | { type: "host:kick"; roomId: string; targetUserId: string }
  | { type: "room:leave"; roomId: string };

export type RoomSocketOptions = {
  roomId: string;
  onMessage: (message: ServerMessage) => void;
  onStatus: (status: RoomSocketStatus) => void;
  onFatal: (code: string, message: string) => void;
};

const NO_RECONNECT_CODES = new Set([
  4000, 4001, 4003, 4004, 4005, 4008, 4009, 4401, 4403, 4404,
]);
const MAX_RECONNECT_ATTEMPTS = 6;

function websocketUrl(): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ws`;
}

function messageId(): string {
  return crypto.randomUUID();
}

export class RoomSocket {
  private readonly options: RoomSocketOptions;
  private socket: WebSocket | null = null;
  private destroyed = false;
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private lastMessageAt = 0;
  private fatalCode = "CONNECTION_LOST";
  private fatalMessage = "Соединение с сервером потеряно.";
  private status: RoomSocketStatus = "connecting";

  constructor(options: RoomSocketOptions) {
    this.options = options;
  }

  connect(): void {
    if (this.destroyed) return;
    this.setStatus(this.reconnectAttempts > 0 ? "reconnecting" : "connecting");
    let socket: WebSocket;
    try {
      socket = new WebSocket(websocketUrl());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;
    socket.addEventListener("open", () => {
      if (this.destroyed) {
        socket.close();
        return;
      }
      this.reconnectAttempts = 0;
      this.lastMessageAt = Date.now();
      this.setStatus("open");
      this.sendRaw({
        type: "room:join",
        roomId: this.options.roomId,
        id: messageId(),
      });
      this.startHeartbeat();
    });

    socket.addEventListener("message", (event) => {
      this.lastMessageAt = Date.now();
      let value: unknown;
      try {
        value = JSON.parse(String(event.data));
      } catch {
        return;
      }
      if (
        !value ||
        typeof value !== "object" ||
        typeof (value as { type?: unknown }).type !== "string"
      ) {
        return;
      }
      const message = value as ServerMessage;
      if (message.type === "error") {
        this.fatalCode = message.code;
        this.fatalMessage = message.message;
      }
      this.options.onMessage(message);
    });

    socket.addEventListener("close", (event) => {
      this.stopHeartbeat();
      if (this.socket === socket) this.socket = null;
      if (this.destroyed) {
        this.setStatus("closed");
        return;
      }
      if (NO_RECONNECT_CODES.has(event.code)) {
        this.fatalCode = this.fatalCode || "CONNECTION_CLOSED";
        this.setStatus("closed");
        this.options.onFatal(this.fatalCode, this.fatalMessage);
        return;
      }
      this.scheduleReconnect();
    });

    socket.addEventListener("error", () => {
      // The close event follows and decides whether to reconnect.
    });
  }

  send(message: OutboundMessage): void {
    if (this.destroyed) return;
    // Signaling is deliberately not queued while offline: after a reconnect
    // the host/viewer rebuilds peers from room:joined, and stale SDP would
    // only create duplicate offers.
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.sendRaw({ ...message, id: messageId() });
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.stopHeartbeat();
    if (this.reconnectTimer != null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      try {
        this.socket.close(1000, "Client closed");
      } catch {
        // Ignore.
      }
      this.socket = null;
    }
    this.setStatus("closed");
  }

  private sendRaw(message: Record<string, unknown>): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    try {
      this.socket.send(JSON.stringify(message));
    } catch {
      // A close event will follow.
    }
  }

  private scheduleReconnect(): void {
    if (this.destroyed) return;
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      this.setStatus("closed");
      this.options.onFatal(
        "CONNECTION_LOST",
        "Не удалось восстановить соединение с сервером.",
      );
      return;
    }
    this.reconnectAttempts += 1;
    const delay = Math.min(1000 * 2 ** (this.reconnectAttempts - 1), 10_000);
    this.setStatus("reconnecting");
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setInterval(() => {
      if (this.socket?.readyState !== WebSocket.OPEN) return;
      if (Date.now() - this.lastMessageAt > 55_000) {
        try {
          this.socket.close(4008, "Heartbeat timeout");
        } catch {
          // Ignore.
        }
        return;
      }
      this.sendRaw({ type: "ping", id: messageId() });
    }, 20_000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer != null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private setStatus(status: RoomSocketStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.options.onStatus(status);
  }
}
